import { Inject, Injectable } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpHeaders,
    HttpInterceptor,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';
import { RuntimeCacheService } from '../runtime-cache.service';
import { plainToInstance } from 'class-transformer';
import { BackendErrorContainerRes } from '../../contracts/v0/cp';
import { Router } from '@angular/router';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { NotificationService } from '../notification-service';
import { TIMESHEET_SIGNATURE_MISSING_ERROR_CODE } from '../../enums/timesheet-signee';
import { catchError, finalize, from, map, Observable, switchMap, throwError, timeout, TimeoutError } from 'rxjs';

const APP_XHR_TIMEOUT = 60000;

@Injectable({
    providedIn: 'root'
})
export class AppInterceptor implements HttpInterceptor {

    constructor(
        private runTimeCacheService: RuntimeCacheService,
        private notificationService: NotificationService,
        private router: Router,
        private translation: L10nTranslationService,
        @Inject(L10N_LOCALE) public locale: L10nLocale,
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(this.performRequest(req)).pipe(
            timeout(APP_XHR_TIMEOUT),
            map((res) => this.handleSuccessfulResponse(res)),
            catchError((err) => this.handleErrorResponse(err)),
            finalize(() => this.handleRequestCompleted())
        );
    }

    private performRequest(req: HttpRequest<any>): HttpRequest<any> {
        let headers: HttpHeaders = req.headers;
        const isFormData = req.body instanceof FormData;

        if (!headers.has('Content-Type') && req.body != null && !isFormData) {
            headers = headers.set('Content-Type', 'application/json');
        }

        if (this.runTimeCacheService.isLogged()) {
            headers = headers.set('Authorization', `Bearer ${this.runTimeCacheService.token}`);
        }

        if (!headers.has('Accept-Language')) {
            headers = headers.set('Accept-Language', this.runTimeCacheService.currentLanguage);
        }

        return req.clone({ url: req.url, headers });
    }

    private handleSuccessfulResponse(event: HttpEvent<any>): HttpEvent<any> {
        if (event instanceof HttpResponse && event?.body?.response !== undefined) {
            return event.clone({ body: event.body.response });
        }
        return event;
    }

    private handleErrorResponse(errorResponse: unknown): Observable<never> {
        if (errorResponse instanceof TimeoutError) {
            this.runTimeCacheService.resetAuthState();
            this.router.navigate(['maintenance']).then();
            return throwError(() => errorResponse);
        }

        const httpError = errorResponse as HttpErrorResponse;

        if (httpError.status === 0) {
            this.runTimeCacheService.resetAuthState();
            this.router.navigate(['maintenance']).then();
            return throwError(() => httpError);
        }

        if (httpError.status === 401) {
            this.notificationService.closeAll();
            this.runTimeCacheService.logout(true);
        }

        if (httpError.error instanceof Blob) {
            return from(httpError.error.text()).pipe(
                switchMap(text => {
                    let payload: unknown = text;
                    if (text) {
                        try {
                            payload = JSON.parse(text);
                        } catch {
                            // Non JSON download errors still fall back to the original HTTP error below.
                        }
                    }
                    return this.handleBackendPayload(httpError, payload);
                }),
            );
        }

        return this.handleBackendPayload(httpError, httpError.error);
    }

    private handleBackendPayload(httpError: HttpErrorResponse, payload: unknown): Observable<never> {
        const normalizedPayload = this.normalizeValidationProblemDetails(httpError, payload);
        const backendError = plainToInstance(
            BackendErrorContainerRes,
            normalizedPayload && typeof normalizedPayload === 'object' ? normalizedPayload : {}
        ) as BackendErrorContainerRes;

        if (backendError?.exception?.errorCode === TIMESHEET_SIGNATURE_MISSING_ERROR_CODE) {
            this.notifySignatureMissing(backendError);
            return throwError(() => backendError);
        }

        if (backendError?.exception || (backendError?.data?.errors?.length ?? 0) > 0) {
            this.notificationService.errorFromBackendError(backendError);
            return throwError(() => backendError);
        }

        const message = this.extractBackendMessage(payload);
        if (message) {
            this.notificationService.errorMessage(message);
        } else {
            this.notificationService.error('generalBackendRequestFailed');
        }

        return throwError(() => httpError);
    }

    /**
     * Performance report export: the selected signee has no signature. Links the current user to their profile
     * when they are affected, otherwise to the user administration where signatures of other users are uploaded.
     */
    private notifySignatureMissing(error: BackendErrorContainerRes): void {
        const errors = error.data?.errors ?? [];
        const currentUserId = Number(this.runTimeCacheService.loginInfo?.id ?? 0);
        const affectsCurrentUser = errors.some(x => Number(x.fieldName) === currentUserId && currentUserId > 0);
        const messages = errors.map(x => x.message).filter(x => !!x?.trim());

        this.notificationService.errorWithAction(
            messages.length ? messages : [this.translation.translate('timesheetSignatureMissing_text')],
            affectsCurrentUser
                ? {route: '/account/profile', label: this.translation.translate('timesheetSignatureMissing_profile_action')}
                : {route: '/administration/accessManagement/user-and-roles', label: this.translation.translate('timesheetSignatureMissing_users_action')},
            this.translation.translate('timesheetSignatureMissing_title'),
            20000,
        );
    }

    private normalizeValidationProblemDetails(httpError: HttpErrorResponse, payload: unknown): unknown {
        if (!payload || typeof payload !== 'object') return payload;
        const source = payload as Record<string, unknown>;
        const rawErrors = source['errors'];
        if (!rawErrors || typeof rawErrors !== 'object' || Array.isArray(rawErrors)) return payload;

        const errors = Object.entries(rawErrors as Record<string, unknown>).flatMap(([fieldName, messages]) =>
            (Array.isArray(messages) ? messages : [messages])
                .filter((message): message is string => typeof message === 'string' && !!message.trim())
                .map(message => ({fieldName, message}))
        );
        if (!errors.length) return payload;

        return {
            statusCode: httpError.status,
            exception: {
                errorCode: httpError.status,
                message: this.extractBackendMessage(payload) ?? this.translation.translate('general_validationFailed'),
            },
            data: {errors},
        };
    }

    private extractBackendMessage(payload: unknown): string | null {
        if (typeof payload === 'string') {
            const message = payload.trim();
            return message || null;
        }

        if (!payload || typeof payload !== 'object') return null;
        const source = payload as Record<string, unknown>;
        for (const key of ['detail', 'message', 'title']) {
            const value = source[key];
            if (typeof value === 'string' && value.trim()) return value.trim();
        }
        return null;
    }

    private handleRequestCompleted(): void {
    }
}